import api from './api'
import type { JournalEntryLine } from './accounting'

export const UHC_HOME_STORES = ['Highland Park', 'River Oaks', 'G&A'] as const
export type UhcHomeStore = (typeof UHC_HOME_STORES)[number]

export interface EmployeeLocation {
  id: number
  homebase_employee_id?: string | null
  homebase_first_name?: string | null
  homebase_last_name?: string | null
  uhc_subscriber_name: string
  home_store: string
  created_at?: string
  updated_at?: string
}

export interface EmployeeLocationCreate {
  homebase_employee_id?: string | null
  homebase_first_name?: string | null
  homebase_last_name?: string | null
  uhc_subscriber_name: string
  home_store: string
}

export interface EmployeeLocationsResponse {
  items: EmployeeLocation[]
  total: number
}

export interface UhcAllocationPreview {
  total: number | string
  highland_park: number | string
  river_oaks: number | string
  g_and_a: number | string
  reallocation_total: number | string
  adj_row_count: number
  current_row_count: number
}

export interface UhcEmployeeLocationsValidation {
  is_valid: boolean
  sections: string[]
  error_message: string
  subscriber_to_home_store: Record<string, string>
  not_in_locations: string[]
  missing_home_store: string[]
}

export interface UhcAuditNote {
  category: string
  message: string
  details?: string[] | null
}

export type UhcAuditStatus = 'ready' | 'ready_with_notes' | 'not_ready'

export interface UhcAuditResponse {
  audit_status: UhcAuditStatus
  has_blockers: boolean
  summary: string
  invoice_number: string
  invoice_date: string
  primary_coverage: string
  detail_row_count: number
  current_row_count: number
  adj_row_count: number
  subscriber_count: number
  employee_locations: UhcEmployeeLocationsValidation
  allocation_before_adjustments: UhcAllocationPreview | null
  allocation_after_adjustments: UhcAllocationPreview | null
  notes: UhcAuditNote[]
}

export interface UhcBankMatch {
  entity_type: string
  qbo_id: string
  txn_date: string
  total_amt: number | string
  aggregated: boolean
  aggregated_count?: number | null
  aggregated_total?: number | string | null
}

export interface UhcValidateBankRequest {
  je_date: string
  amount_paid: number | string
}

export interface UhcValidateBankResponse {
  matched: boolean
  match?: UhcBankMatch | null
  diagnostics: string
  message: string
}

export interface UhcMonthSummaryItem {
  report_month: string
  status: 'complete' | 'incomplete'
  hint?: string | null
}

export interface UhcInvoiceFileItem {
  id: number
  kind: 'invoice' | 'adjustment'
  filename: string
  created_at: string
}

export interface UhcMonthDetailResponse {
  report_month: string
  status: 'complete' | 'incomplete'
  files: UhcInvoiceFileItem[]
  audit: UhcAuditResponse | null
  invoice_date: string | null
  je_date: string | null
  draft: UhcDraftResponse | null
}

export interface UhcDraftResponse {
  id: number | null
  lines: JournalEntryLine[]
  warnings: string[]
  private_note: string
  doc_number: string
  txn_date: string
  source_data: Record<string, unknown>
  total_debits: number | string
  total_credits: number | string
  status: string
  qbo_url?: string | null
  qbo_entity_id?: string | null
  charge_by_store: Record<string, number | string>
  total_charge_amount: number | string
}

export interface EmployeeLocationCsvImportResponse {
  success: boolean
  rows_processed: number
  rows_upserted: number
  rows_skipped: number
  errors: string[]
}

export async function getEmployeeLocations(): Promise<EmployeeLocationsResponse> {
  const response = await api.get<EmployeeLocationsResponse>(
    '/uhc-allocations/employee-locations'
  )
  return response.data
}

export async function createEmployeeLocation(
  data: EmployeeLocationCreate
): Promise<EmployeeLocation> {
  const response = await api.post<EmployeeLocation>(
    '/uhc-allocations/employee-locations',
    data
  )
  return response.data
}

export async function updateEmployeeLocation(
  id: number,
  data: Partial<EmployeeLocationCreate>
): Promise<EmployeeLocation> {
  const response = await api.put<EmployeeLocation>(
    `/uhc-allocations/employee-locations/${id}`,
    data
  )
  return response.data
}

export async function deleteEmployeeLocation(id: number): Promise<void> {
  await api.delete(`/uhc-allocations/employee-locations/${id}`)
}

export async function importEmployeeLocationsCsv(
  file: File
): Promise<EmployeeLocationCsvImportResponse> {
  const form = new FormData()
  form.append('file', file)
  const response = await api.post<EmployeeLocationCsvImportResponse>(
    '/uhc-allocations/employee-locations/import-csv',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return response.data
}

export async function auditUhcInvoice(file: File): Promise<UhcAuditResponse> {
  const form = new FormData()
  form.append('file', file)
  const response = await api.post<UhcAuditResponse>('/uhc-allocations/audit', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

export async function validateUhcBankTransaction(
  data: UhcValidateBankRequest
): Promise<UhcValidateBankResponse> {
  const response = await api.post<UhcValidateBankResponse>(
    '/uhc-allocations/validate-bank',
    data
  )
  return response.data
}

export async function getUhcMonthsSummary(): Promise<{ months: UhcMonthSummaryItem[] }> {
  const response = await api.get<{ months: UhcMonthSummaryItem[] }>(
    '/uhc-allocations/months-summary',
  )
  return response.data
}

export async function getUhcMonth(reportMonth: string): Promise<UhcMonthDetailResponse> {
  const response = await api.get<UhcMonthDetailResponse>(
    `/uhc-allocations/months/${reportMonth}`,
  )
  return response.data
}

export async function uploadUhcMonthFile(
  reportMonth: string,
  file: File,
  kind: 'invoice' | 'adjustment',
): Promise<UhcInvoiceFileItem> {
  const form = new FormData()
  form.append('file', file)
  form.append('kind', kind)
  const response = await api.post<UhcInvoiceFileItem>(
    `/uhc-allocations/months/${reportMonth}/files`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return response.data
}

export async function deleteUhcMonthFile(reportMonth: string, fileId: number): Promise<void> {
  await api.delete(`/uhc-allocations/months/${reportMonth}/files/${fileId}`)
}

export async function downloadUhcMonthFile(reportMonth: string, fileId: number): Promise<Blob> {
  const response = await api.get(`/uhc-allocations/months/${reportMonth}/files/${fileId}`, {
    responseType: 'blob',
  })
  return response.data as Blob
}

export async function auditUhcMonth(reportMonth: string): Promise<UhcAuditResponse> {
  const response = await api.post<UhcAuditResponse>(
    `/uhc-allocations/months/${reportMonth}/audit`,
  )
  return response.data
}

export async function deleteUhcJournalEntryFromQbo(
  draftId: number,
): Promise<{ draft: UhcDraftResponse }> {
  const response = await api.post<{ draft: UhcDraftResponse }>(
    `/uhc-allocations/drafts/${draftId}/delete-qbo`,
  )
  return response.data
}

export async function createUhcMonthDraft(
  reportMonth: string,
  jeDate?: string,
): Promise<UhcDraftResponse> {
  const form = new FormData()
  if (jeDate) form.append('je_date', jeDate)
  const response = await api.post<UhcDraftResponse>(
    `/uhc-allocations/months/${reportMonth}/draft`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return response.data
}
